/**
 * Consignment payout state → UI meaning (spec_20260817). Two consumers, both
 * treating the server as the authority. The Consignments page (the ledger)
 * renders every payout state and its countdown via payoutStateChip /
 * saleStatusChip. The Payments page shows only PAYABLE payouts, because the
 * awaiting_payment queue is eligibility-filtered server-side (Design B,
 * 2026-09-15) — pre-eligible and refund_review payouts never reach it — so
 * consignmentBlockReason there only has to catch a missing payout amount.
 */

/**
 * Parse a split % that may arrive as a HubSpot empty string for an unset
 * term. `''` and non-numeric values → null so ConsignmentBadge renders
 * "terms unknown" rather than `Number('') === 0` → "seller receives 0%".
 * D16: the split is the seller's share; a blank is unknown, never zero.
 */
export function parseSplitPct(value) {
    if (value == null || value === '') return null;
    const pct = Number(value);
    return Number.isFinite(pct) ? pct : null;
}

const STATE_CHIPS = {
    eligible: { label: 'Eligible', badgeClass: 'badge-success' },
    legacy_unlinked: { label: 'Eligible (legacy)', badgeClass: 'badge-success' },
    awaiting_shipment: { label: 'Awaiting shipment', badgeClass: 'badge-warning' },
    in_return_window: { label: 'In return window', badgeClass: 'badge-info' },
    refund_review: { label: 'Refund review', badgeClass: 'badge-error' },
    paid: { label: 'Paid', badgeClass: 'badge-neutral' },
};

const ATTENTION_LABELS = {
    refund_review: 'Refund review',
    refund_after_payout: 'Refund after payout',
    no_sale_out_of_stock: 'Out of stock, no sale',
    unshipped_stalled: 'Unshipped 14+ days',
    split_mismatch: 'Split mismatch',
    // Mark as Unsold (spec 20261016)
    sold_after_unsold: 'Sold after marked unsold: stop the return',
    unsold_back_in_stock: 'Unsold but back in stock',
};

/**
 * Why this awaiting-payment row cannot be selected for payment, or null when it
 * can. Since Design B (2026-09-15) the awaiting_payment queue is
 * eligibility-filtered server-side, so any consignment row that reaches the
 * Payments page is already payable — the only local block is a missing payout
 * amount (a legacy row with no payout recorded). Payout state and countdowns
 * live on the Consignments page, not here.
 */
export function consignmentBlockReason(deal) {
    if (!deal?.isConsignment) return null;
    if (deal.payoutMissing) return 'No payout recorded';
    return null;
}

/** Chip for a payout state string off the API; null when there is no payout. */
export function payoutStateChip(state, daysRemaining) {
    if (!state) return null;
    if (state === 'in_return_window') {
        return { label: eligibleCountdown(daysRemaining), badgeClass: 'badge-info' };
    }
    const chip = STATE_CHIPS[state];
    return chip ?? { label: state, badgeClass: 'badge-neutral' };
}

/** Chip for a sale's live order status ({shipped, refunded}); null when unsold. */
export function saleStatusChip(sale) {
    if (!sale) return null;
    if (sale.refunded) return { label: 'Refunded', badgeClass: 'badge-error' };
    if (sale.shipped) return { label: 'Shipped', badgeClass: 'badge-success' };
    return { label: 'To be shipped', badgeClass: 'badge-warning' };
}

/** Human label for an attention flag; unknown flags pass through raw. */
export function attentionLabel(flag) {
    if (flag.startsWith('blocked:')) return `Blocked: ${flag.slice('blocked:'.length)}`;
    return ATTENTION_LABELS[flag] ?? flag;
}

function eligibleCountdown(daysRemaining) {
    const days = Number(daysRemaining);
    return `Eligible in ${days} ${days === 1 ? 'day' : 'days'}`;
}
